import { useEffect, useState } from 'react';
import { CalendarClockIcon, Loader2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { HubMonitoring } from '@/services/field.service';
import { useUpdateHubSettings } from '@/services/hub.service';
import { saveErrorMessage } from './saveError';
import {
    dailyToCron,
    describeCron,
    hoursToCron,
    isCronSyntax,
    normalizeCron,
    parseCron,
} from './cron';

/**
 * How often the hub wakes up to acquire — the `acquisition_schedule` of its
 * Hub.json, and the schedule every dot on this page is judged against. It sits
 * among the telemetry but is a setting, so it is shaped like the code-update
 * and SIM buttons beside it: something to press, not a number to read.
 */
export default function HubSchedule({
    hub,
    bare = false,
}: {
    hub: Pick<HubMonitoring, 'macAddress' | 'acquisitionSchedule'>;
    /** The button alone, without its caption, for a table cell. */
    bare?: boolean;
}) {
    const [editing, setEditing] = useState(false);
    // Holds the just-saved cron until the monitoring query catches up — it
    // re-reads every hub's S3 history, so that is a long moment to sit showing
    // the schedule the hub is no longer on.
    const [saved, setSaved] = useState<string | null>(null);
    const current = saved ?? hub.acquisitionSchedule;

    useEffect(() => {
        if (saved !== null && hub.acquisitionSchedule === saved) {
            setSaved(null);
        }
    }, [hub.acquisitionSchedule, saved]);

    const label = current ? describeCron(current) : null;

    return (
        <div className={bare ? '' : 'flex flex-col items-end'}>
            {!bare && (
                <div className="text-[10px] uppercase tracking-wider text-[#6b7480]">
                    Schedule
                </div>
            )}
            <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
                title={
                    'How often the hub wakes up to acquire ' +
                    "(acquisition_schedule in the hub's Hub.json), and what " +
                    'every dot below is judged against. Click to change it on ' +
                    'the hub.' +
                    (current ? `\ncron: ${current}` : '')
                }
                className="h-6 px-2 mt-0.5 text-sm bg-[#0f141a] border-[#1e242a] text-[#d8dde2] hover:bg-[#1a2126] hover:text-white"
            >
                <CalendarClockIcon className="h-3 w-3 mr-1 text-[#6b7480]" />
                {/* A schedule we can draw no grid for still has to be readable
                    here: the hub is running it either way. */}
                {label ?? (
                    <span className="font-mono text-xs">{current || '—'}</span>
                )}
            </Button>

            <Dialog open={editing} onOpenChange={setEditing}>
                <DialogContent className="max-w-lg bg-[#161c22] border-[#1e242a] text-[#d8dde2]">
                    <DialogHeader>
                        <DialogTitle className="text-white">
                            Acquisition schedule{' '}
                            <span className="font-mono text-sm text-[#a8b0bb] ml-2">
                                {hub.macAddress}
                            </span>
                        </DialogTitle>
                    </DialogHeader>
                    {/* Remounts each time, so a previous edit's half-typed
                        draft and its error are gone. */}
                    {editing && (
                        <ScheduleForm
                            macAddress={hub.macAddress}
                            current={current}
                            onSaved={(cron) => {
                                setSaved(cron);
                                setEditing(false);
                            }}
                            onCancel={() => setEditing(false)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

/** The three ways of saying a schedule, in the order the form offers them. */
type Mode = 'hours' | 'daily' | 'cron';

const MODE_LABELS: Record<Mode, string> = {
    hours: 'Every n hours',
    daily: 'Daily at',
    cron: 'Cron',
};

function ScheduleForm({
    macAddress,
    current,
    onSaved,
    onCancel,
}: {
    macAddress: string;
    current: string;
    onSaved: (cron: string) => void;
    onCancel: () => void;
}) {
    const save = useUpdateHubSettings();
    const shape = parseCron(normalizeCron(current));
    // Open on the simplest form that can say what the hub is already on, so
    // the schedule in front of you is the one you are editing. A cron the two
    // pickers can't express — a sub-hour interval, an offset minute, anything
    // stranger — opens as itself rather than as something near it.
    const [mode, setMode] = useState<Mode>(() => {
        if (shape?.kind === 'hours' && shape.minute === 0) return 'hours';
        if (shape?.kind === 'daily') return 'daily';
        return 'cron';
    });

    const [hours, setHours] = useState(
        shape?.kind === 'hours' ? String(shape.hours) : '6',
    );
    const [hour, setHour] = useState(
        shape?.kind === 'daily' ? String(shape.hour) : '6',
    );
    const [minute, setMinute] = useState(
        shape?.kind === 'daily' ? String(shape.minute) : '0',
    );
    const [cron, setCron] = useState(current);

    const draft = buildCron(mode, { hours, hour, minute, cron });
    const valid = draft !== null && isCronSyntax(draft);
    const normalized = valid ? normalizeCron(draft) : null;
    const label = normalized ? describeCron(normalized) : null;

    const commit = () => {
        if (!normalized) return;
        if (normalized === normalizeCron(current)) {
            onCancel();
            return;
        }
        // Always as cron, whichever picker wrote it: the modes are three ways
        // of typing one setting, not three settings.
        save.mutate(
            {
                macAddress,
                settings: { acquisitionScheduleCron: normalized },
            },
            { onSuccess: () => onSaved(normalized) },
        );
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') commit();
        else if (event.key === 'Escape') onCancel();
    };

    return (
        <div className="flex flex-col gap-5">
            <div className="flex flex-wrap gap-2">
                {(Object.keys(MODE_LABELS) as Mode[]).map((key) => (
                    <ModeChip
                        key={key}
                        label={MODE_LABELS[key]}
                        selected={mode === key}
                        onClick={() => {
                            // Carry what is on screen into the cron box, so
                            // switching to it starts from the schedule being
                            // edited rather than from the saved one.
                            if (key === 'cron' && normalized) {
                                setCron(normalized);
                            }
                            setMode(key);
                        }}
                    />
                ))}
            </div>

            {mode === 'hours' && (
                <Field label="Wake up every">
                    <NumberInput
                        autoFocus
                        value={hours}
                        min={1}
                        max={24}
                        onChange={setHours}
                        onKeyDown={onKeyDown}
                    />
                    <span className="text-sm text-[#a8b0bb]">
                        {hours === '1' ? 'hour' : 'hours'}
                    </span>
                </Field>
            )}

            {mode === 'daily' && (
                <Field label="Wake up once a day, at">
                    <NumberInput
                        autoFocus
                        value={hour}
                        min={0}
                        max={23}
                        onChange={setHour}
                        onKeyDown={onKeyDown}
                    />
                    <span className="text-sm text-[#a8b0bb]">h</span>
                    <NumberInput
                        value={minute}
                        min={0}
                        max={59}
                        onChange={setMinute}
                        onKeyDown={onKeyDown}
                    />
                    <span className="text-sm text-[#a8b0bb]">min UTC</span>
                </Field>
            )}

            {mode === 'cron' && (
                <div className="flex flex-col gap-1.5">
                    <label
                        htmlFor="hub-schedule-cron"
                        className="text-xs text-[#a8b0bb]"
                    >
                        Cron expression
                    </label>
                    <input
                        id="hub-schedule-cron"
                        autoFocus
                        value={cron}
                        maxLength={120}
                        spellCheck={false}
                        placeholder="0 */6 * * *"
                        onChange={(event) => setCron(event.target.value)}
                        onKeyDown={onKeyDown}
                        className={
                            'px-2 py-1.5 rounded text-sm font-mono bg-[#0f141a] border text-[#d8dde2] placeholder:text-[#6b7480] focus:outline-none ' +
                            (valid || cron.trim() === ''
                                ? 'border-[#2d2d2d] focus:border-[#74c69d]'
                                : 'border-[#d9534f]')
                        }
                    />
                    <p className="text-[11px] text-[#6b7480]">
                        Five fields — minute, hour, day, month, weekday — in
                        UTC. Written to the hub as typed.
                    </p>
                </div>
            )}

            {/* What will be written, always in the hub's own terms, so the two
                pickers are never a black box either. */}
            <div className="rounded border border-[#1e242a] bg-[#0f141a] px-3 py-2 text-xs">
                {normalized ? (
                    <>
                        <span className="font-mono text-[#d8dde2]">
                            {normalized}
                        </span>
                        <span className="text-[#6b7480]">
                            {label
                                ? ` — ${label}, UTC`
                                : ' — a schedule the monitoring grid cannot be' +
                                  ' drawn for: the hub will run it, but its' +
                                  ' dots below will read as unplanned.'}
                        </span>
                    </>
                ) : (
                    <span className="text-[#ff8a84]">
                        Not a cron expression — nothing to save yet.
                    </span>
                )}
            </div>

            {save.isError && (
                <p className="text-sm text-[#ff8a84]">
                    Not saved — {saveErrorMessage(save.error)}
                </p>
            )}

            <div className="flex justify-end gap-2">
                <Button
                    size="sm"
                    variant="outline"
                    disabled={save.isPending}
                    onClick={onCancel}
                    className="bg-transparent border-[#1e242a] text-[#a8b0bb] hover:bg-[#1a2126] hover:text-white"
                >
                    Cancel
                </Button>
                <Button
                    size="sm"
                    disabled={save.isPending || !normalized}
                    onClick={commit}
                    className="bg-[#0f141a] border border-[#1e242a] text-[#d8dde2] hover:bg-[#1a2126] hover:text-white"
                >
                    {save.isPending && (
                        <Loader2Icon className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    )}
                    Save
                </Button>
            </div>
        </div>
    );
}

/** The cron the current mode's inputs add up to, or null while they don't. */
function buildCron(
    mode: Mode,
    draft: { hours: string; hour: string; minute: string; cron: string },
): string | null {
    if (mode === 'cron') return draft.cron.trim() === '' ? null : draft.cron;
    if (mode === 'hours') {
        const n = inRange(draft.hours, 1, 24);
        return n === null ? null : hoursToCron(n);
    }
    const h = inRange(draft.hour, 0, 23);
    const m = inRange(draft.minute, 0, 59);
    return h === null || m === null ? null : dailyToCron(h, m);
}

function inRange(raw: string, min: number, max: number): number | null {
    const n = Number.parseInt(raw, 10);
    if (!Number.isInteger(n) || n < min || n > max) return null;
    return n;
}

function Field({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-xs text-[#a8b0bb]">{label}</span>
            <div className="flex items-center gap-2">{children}</div>
        </div>
    );
}

function NumberInput({
    value,
    min,
    max,
    autoFocus,
    onChange,
    onKeyDown,
}: {
    value: string;
    min: number;
    max: number;
    autoFocus?: boolean;
    onChange: (value: string) => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
}) {
    const valid = inRange(value, min, max) !== null;
    return (
        <input
            type="number"
            min={min}
            max={max}
            value={value}
            autoFocus={autoFocus}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={onKeyDown}
            className={
                'h-7 w-16 px-1.5 rounded text-sm text-center tabular-nums bg-[#0f141a] border text-[#d8dde2] focus:outline-none [color-scheme:dark] ' +
                (valid
                    ? 'border-[#2d2d2d] focus:border-[#74c69d]'
                    : 'border-[#d9534f]')
            }
        />
    );
}

function ModeChip({
    label,
    selected,
    onClick,
}: {
    label: string;
    selected: boolean;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            className={
                'px-3 py-1 rounded-full text-xs border transition-colors ' +
                (selected
                    ? 'bg-[#1a2620] border-[#74c69d] text-[#74c69d]'
                    : 'bg-[#0f141a] border-[#2d2d2d] text-[#a8b0bb] hover:text-white hover:border-[#3a4249]')
            }
        >
            {label}
        </button>
    );
}
